/**
 * VariantFieldContext
 * --------------------
 * Schema-agnostic version of the AB-variant field-disable logic originally
 * built for VolumeDiscount's BundleEditorContext. Any bundle editor that
 * wires RHF + A/B variants can provide this context so FormFields/* fields
 * automatically disable themselves on non-primary variants for fields that
 * are always sourced from the primary/base document (`skipKeys`).
 *
 * Consumers with their own strongly-typed skip-key list (e.g. keyof some
 * config interface) should provide a thin wrapper context that supplies
 * `skipKeys` as `string[]` here, and can re-export `useVariantFieldDisabled`
 * under their own name for backwards compatibility.
 */

import { createContext, useContext } from "react";

export interface VariantEditorVariant {
    id: string;
}

export interface VariantFieldContextType {
    activeTarget: string;
    variants: VariantEditorVariant[];
    abEnabled: boolean;
    /** Top-level field-path segments whose value always comes from the primary variant / base document. */
    skipKeys: string[];
}

export const VariantFieldContext = createContext<VariantFieldContextType | null>(null);

export function useVariantFieldDisabled(fieldName?: string): boolean {
    const context = useContext(VariantFieldContext);
    if (!context) return false;

    const { abEnabled, activeTarget, variants, skipKeys } = context;
    if (!abEnabled) return false;

    // Check if the current variant is the first/primary variant
    const isPrimary = variants.length > 0 && activeTarget === variants[0].id;
    if (isPrimary) return false;

    // If no field name is provided, just return whether we're on a non-primary variant.
    if (!fieldName) return true;

    // Determine the top-level path segment of the field name
    // e.g. "general_settings.show_on_product_page" -> "general_settings"
    const topLevelKey = fieldName.split(".")[0];
    return skipKeys.includes(topLevelKey);
}
